/**
 * Web chat tool-card for the Skill run / create / publish tools (M2.5).
 *
 * - skillRun     → direct dry-run/apply result + tier badge. Historical pending
 *                  runs still render {@link ApprovalBanner} so old threads work.
 * - skillCreate  → "Created skill" confirmation.
 * - skillPublish → approval-gated publish confirmation.
 *
 * Web-only (a `.native.tsx` sibling renders the iOS/Android version). Reuses the
 * web Skills Hub presentational bits so the diff matches the Hub run dialog.
 */
import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { CardShell } from './card-shell';
import { ApprovalBanner } from './approval-banner';
import { safeGet } from './types';
import type { ToolCardProps } from './types';
import {
  SkillDiffView,
  SkillTierBadge,
  skillDiffIsEmpty,
} from '../../skills/skill-ui.web';
import {
  extractRunDiff,
  extractSkillRunFailure,
} from '../../skills/skill-diff';

export const SkillRunCard: FC<ToolCardProps> = ({
  toolName,
  result,
  display,
  stage,
  approvalId,
  isError,
}) => {
  const { t } = useTranslation('common');

  const isCreate = toolName === 'skillCreate';
  const isPublish = toolName === 'skillPublish';

  const name =
    safeGet<string>(result, 'name') ??
    safeGet<string>(result, 'run', 'skillId') ??
    display?.title ??
    '';
  const tier = safeGet<string>(result, 'tier');
  const description = safeGet<string>(result, 'description');

  const eyebrow = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <MaterialSymbol name="bolt" size={15} style={{ color: 'var(--text-tertiary)' }} />
      <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-tertiary)' }}>
        {t('skills.card.label')}
      </span>
    </div>
  );

  if (stage === 'pending_approval' && approvalId) {
    return (
      <CardShell>
        <div style={{ padding: '16px 18px 4px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {eyebrow}
          <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }} dir="auto">
            {name || (isPublish ? t('skills.card.publishTitle') : t('skills.card.runTitle'))}
          </div>
        </div>
        <ApprovalBanner approvalId={approvalId} toolName={toolName} />
      </CardShell>
    );
  }

  const failure = extractSkillRunFailure({ result, stage, isError });
  if (failure !== null) {
    return (
      <CardShell>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {eyebrow}
          <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }}>
            {name || t('skills.card.runTitle')}
          </div>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)' }}>
            {failure || t('skills.runDialog.failed')}
          </p>
        </div>
      </CardShell>
    );
  }

  if (isCreate || isPublish) {
    return (
      <CardShell>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <MaterialSymbol name="check_circle" size={16} style={{ color: 'var(--success, #1a7f37)' }} />
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-tertiary)' }}>
              {isCreate ? t('skills.card.created') : t('skills.card.published')}
            </span>
          </div>
          {name ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }} dir="auto">
                {name}
              </span>
              {tier ? <SkillTierBadge tier={tier} /> : null}
            </div>
          ) : null}
          {isCreate && description ? (
            <p
              dir="auto"
              style={{
                margin: 0,
                fontSize: 14,
                lineHeight: '20px',
                color: 'var(--text-secondary)',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {description}
            </p>
          ) : null}
        </div>
      </CardShell>
    );
  }

  // skillRun (completed) → diff preview.
  const diff = extractRunDiff(result);
  const isDryRun = safeGet<boolean>(result, 'run', 'dryRun') ?? true;
  const isApplied = Boolean(safeGet<string>(result, 'run', 'resultCvId'));
  const empty = skillDiffIsEmpty(diff);

  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <MaterialSymbol name="auto_awesome" size={15} style={{ color: 'var(--text-tertiary)' }} />
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-tertiary)' }}>
              {t('skills.card.label')}
            </span>
          </div>
          {isApplied ? (
            <span
              className="tabbio-menu-badge"
              style={{ color: 'var(--success, #1a7f37)' }}
            >
              {t('skills.card.appliedBadge')}
            </span>
          ) : isDryRun ? (
            <span className="tabbio-menu-badge">{t('skills.card.previewBadge')}</span>
          ) : null}
        </div>

        {name ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }} dir="auto">
              {name}
            </span>
            {tier ? <SkillTierBadge tier={tier} /> : null}
          </div>
        ) : null}

        {empty ? (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)' }}>
            {t('skills.runDialog.noChanges')}
          </p>
        ) : (
          <SkillDiffView diff={diff} />
        )}
      </div>
    </CardShell>
  );
};
